import React from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';

import AddAssetElementModal from '@atom/components/common/elements/AddAssetElementModal';
import ElementsHeader from '@atom/components/common/elements/ElementsHeader';
import MoveElementModal from '@atom/components/common/moveElement/MoveElementModal';
import RenameModal from '@atom/components/common/RenameModal';
import ReplaceElementModal from '@atom/components/common/replaceElement/ReplaceElementModal';
import {
  Collapse,
  Icon,
  IconButton,
  List,
  Menu,
  Modal,
  Progress,
  Tooltip,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import fontStyles from '@atom/styles/fonts';
import { AttributesType, ChangeType } from '@atom/types/inventory';
import { PolicyAction } from '@atom/types/policy';
import { Preferences } from '@atom/types/preferences';
import { InventoryAssetDetailState, ReduxStore } from '@atom/types/store';
import { hasAccess } from '@atom/utilities/accessUtilities';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  Client,
  isCurrentClient,
} from '@atom/utilities/featureToggleUtilities';
import iconUtilities from '@atom/utilities/iconUtilities';

import classes from '../../../common/elements/elements.module.css';

const { ListItemButton, ListItemText } = List;
const { MenuItem } = Menu;

const styles = {
  listStyle: {
    padding: '0',
  },
  selectedListItem: {
    boxSizing: 'border-box',
    backgroundColor: colors.utility.highlight,
    borderLeft: `3px solid ${colors.brand.blue}`,
    fontSize: fontStyles.md,
    height: '3.125rem',
    paddingTop: '0.6rem',
  },
  listItem: {
    boxSizing: 'border-box',
    borderLeft: '3px solid transparent',
    fontSize: fontStyles.md,
    height: '3.125rem',
    paddingTop: '0.6rem',
  },
  iconColor: colors.brand.blue,
  listItemTextStyle: { fontWeight: 0, marginLeft: '10px' },
  elementNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '70%',
  },
  elementRightIconContainer: {
    position: 'absolute',
    right: '1.25rem',
    display: 'flex',
    alignItems: 'center',
  },
  elementLabel: {
    padding: '0',
    margin: '0',
    marginBottom: '2px',
    fontWeight: '500',
  },
  editedAddedElementLabel: {
    padding: '0',
    margin: '0',
    marginBottom: '2px',
    fontWeight: '500',
    color: colors.brand.blue,
  },
  deletedElementLabel: {
    padding: '0',
    margin: '0',
    marginBottom: '2px',
    fontWeight: '500',
    textDecoration: 'line-through',
    textDecorationColor: colors.brand.blue,
  },
  selectedGroupStyle: {
    color: colors.brand.blue,
    fontWeight: '500',
  },
  assetType: {
    padding: '0',
    margin: '0',
    marginTop: '2px',
    color: colors.neutral.gray,
    fontSize: fontStyles.sm,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    minWidth: 0,
  },
};

enum ModalType {
  REJECT_EDIT = 'REJECT_EDIT',
  ADD_ELEMENT = 'ADD_ELEMENT',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  NONE = 'NONE',
  MOVE_ELEMENT = 'MOVE_ELEMENT',
  REPLACE_ELEMENT = 'REPLACE_ELEMENT',
}

interface ReduxStateProps {
  preferences: Preferences;
}

interface PassedProps {
  inventoryAssetDetail: InventoryAssetDetailState;
  navigateToElement: (element: Object) => void;
  loading: boolean;
  selectedItem: any;
  onPendingApproval: (action: string, element: any) => void;
  updateSelectedItem: (item: Object, type: string) => void;
  onRenameAction: (payload: Object) => void;
  onDeleteAction: (payload: Object) => void;
  navigateToAsset: (asset: Object) => void;
  onBatchCreate: (payload: Object) => void;
  onToggle: (
    id: string,
    path: any[],
    expanded: boolean,
    hasChildren: any,
  ) => void;
  onMoveElement: () => void;
}

type Props = PassedProps & ReduxStateProps;

class DetailTree extends React.Component<Props> {
  state: {
    openModal: { type: ModalType; elementOrGroupId: any };
  } = { openModal: { type: ModalType.NONE, elementOrGroupId: null } };

  toggleModal = (modalToOpen: ModalType, id: any) => {
    this.setState({ openModal: { type: modalToOpen, elementOrGroupId: id } });
  };

  isModalOpen = (modalType: ModalType, id: any) => {
    const { type, elementOrGroupId } = this.state.openModal;
    return type === modalType && elementOrGroupId === id;
  };

  navigate = (element: any) => {
    if (!R.isNil(element.isGroup) && element.isGroup) {
      return;
    }

    const { navigateToElement, updateSelectedItem } = this.props;
    updateSelectedItem(element, 'element');
    navigateToElement(element);
  };

  selectAsset = () => {
    const { inventoryAssetDetail, updateSelectedItem, navigateToAsset } =
      this.props;
    navigateToAsset(inventoryAssetDetail);
    updateSelectedItem(inventoryAssetDetail, 'asset');
  };

  getAttributeSubText = (element: any): string => {
    if (
      R.isNil(element.attributes) ||
      R.isEmpty(element.attributes) ||
      !Array.isArray(element.attributes)
    ) {
      return '';
    }

    return element.attributes.reduce(
      (acc: string, attribute: AttributesType): string => {
        const label = attribute.name;
        const value = attributeDisplayUtility.display(
          attribute.value,
          attribute.dataType,
          attribute.unit,
        );

        const text = `${label}: ${value}`;
        return acc ? `${acc}, ${text}` : text;
      },
      '',
    );
  };

  onPendingApproval = (action: string, element: object) => {
    const { onPendingApproval } = this.props;

    onPendingApproval(action, element);
  };

  getDisplayLabelStyle = (
    isPendingApproval: boolean,
    changeType: ChangeType,
  ) => {
    if (!isPendingApproval) {
      return styles.elementLabel;
    }

    if (changeType === ChangeType.DELETED) {
      return styles.deletedElementLabel;
    }

    if (
      changeType === ChangeType.ADDED ||
      changeType === ChangeType.EDITED ||
      changeType === ChangeType.ELEMENTS_CHANGED ||
      changeType === ChangeType.PARENT_ADDED
    ) {
      return styles.editedAddedElementLabel;
    }

    return styles.elementLabel;
  };

  getGroupStyle = (elementGroup: any) => {
    let style = {};
    if (!elementGroup || !elementGroup.isGroup) {
      return style;
    }

    elementGroup.elements.forEach(element => {
      if (element.changeType) {
        style = styles.selectedGroupStyle;
      }
    });

    return style;
  };

  getElementPendingStateControls = (element: any, id: any) => {
    const onConfirmRejectChangesModal = event => {
      event.stopPropagation();
      this.onPendingApproval('reject', element);
      this.toggleModal(ModalType.NONE, null);
    };

    return (
      <div className={classes.pendingApprovalContainer}>
        <div className={classes.pendingElementLabel}>{element.changeType}</div>
        <div className={classes.pendingControlsContainer}>
          <div className={classes.pendingElementControl}>
            <IconButton
              tooltip="Reject"
              onClick={() => this.toggleModal(ModalType.REJECT_EDIT, id)}
            >
              <Icon color={styles.iconColor}>clear</Icon>
            </IconButton>
            <Modal
              open={this.isModalOpen(ModalType.REJECT_EDIT, id)}
              title="Reject Changes"
              onCancel={() => this.toggleModal(ModalType.NONE, null)}
              confirmButtonText="Reject"
              onConfirm={onConfirmRejectChangesModal}
            >
              Are you sure you want to reject the changes on this item?
            </Modal>
          </div>
          <div className={classes.pendingElementControl}>
            <IconButton
              tooltip="Approve"
              onClick={() => this.onPendingApproval('accept', element)}
            >
              <Icon color={styles.iconColor}>check</Icon>
            </IconButton>
          </div>
        </div>
      </div>
    );
  };

  renderNestedElements = (
    elements: any[],
    elementPath: any[],
    level: number = 0,
    parentSchemaId: string = '',
  ): any[] => {
    if (R.isEmpty(elements) || R.isNil(elements)) {
      return [];
    }

    return elements.map((element: any, index: number) => {
      const {
        onToggle,
        selectedItem,
        updateSelectedItem,
        inventoryAssetDetail,
        preferences,
        onMoveElement,
      } = this.props;
      const nestedAsset =
        R.isNil(element.isGroup) || element.isGroup
          ? element
          : R.pathOr([{}], ['elements'], element)[0];

      const { hasChildren } = nestedAsset;

      const { name, expanded, markerId } = element;

      const id = R.pathOr('', ['id'], element);

      const nestedElements = R.pathOr([], ['elements'], element);
      const nextElements =
        nestedElements[0]?.id === element.id ? [] : nestedElements;

      const path = [...elementPath, 'elements', index];

      const rootStyles =
        selectedItem.id === id ? styles.selectedListItem : styles.listItem;

      const isPendingApproval = !!nestedAsset.changeType;

      const onRename = (elementId: string, elementName: string): boolean => {
        const { onRenameAction } = this.props;

        onRenameAction({
          id: elementId,
          name: elementName,
          elementPath: path,
        });

        return true;
      };

      const onDelete = (): boolean => {
        const { onDeleteAction } = this.props;

        onDeleteAction({
          id,
          elementPath: path,
        });

        return true;
      };

      const onConfirmDeleteModal = () => {
        onDelete();
        this.toggleModal(ModalType.NONE, null);
      };

      const onMoveElementClose = () => {
        onMoveElement();
        this.toggleModal(ModalType.NONE, null);
      };

      const hasPendingDelete = element.changeType === ChangeType.DELETED;
      const canRename = hasAccess(
        PolicyAction.RENAME,
        inventoryAssetDetail.actions,
      );
      const hasAddAssetRolePermission = isCurrentClient([
        Client.UDOT_TMD,
        Client.ARDOT,
      ])
        ? hasRolePermissions(ROLE_SETS.ORG_ADMIN)
        : true;
      const canUpdate =
        hasAddAssetRolePermission &&
        hasAccess(PolicyAction.UPDATE, inventoryAssetDetail.actions);

      const moveElementEnabled = R.pathOr(
        false,
        ['inventory', 'moveElementEnabled'],
        preferences,
      );

      const replaceElementEnabled = R.pathOr(
        false,
        ['inventory', 'replaceElementEnabled'],
        preferences,
      );

      const showDelete = canUpdate && !hasPendingDelete;
      const showRename = canRename && !hasPendingDelete;
      const showMove =
        moveElementEnabled &&
        nestedAsset?.changeType !== ChangeType.ADDED &&
        hasAccess(PolicyAction.MOVE_ELEMENT, inventoryAssetDetail.actions);
      const showReplace =
        replaceElementEnabled &&
        nestedAsset?.changeType !== ChangeType.ADDED &&
        hasAccess(PolicyAction.REPLACE_ELEMENT, inventoryAssetDetail.actions);

      const canManageChanges = hasAccess(
        PolicyAction.MANAGE_INVENTORY_CHANGES,
        inventoryAssetDetail.actions,
      );

      const showChangeControls =
        canManageChanges &&
        isPendingApproval &&
        nestedAsset.changeType !== ChangeType.ELEMENTS_CHANGED &&
        nestedAsset.changeType !== ChangeType.PARENT_ADDED;

      const showOptions =
        showRename || showDelete || canUpdate || showMove || showReplace;

      const rightIconButton = (
        <div
          className={classes.elementRightIconContainer}
          style={styles.elementRightIconContainer}
        >
          {showChangeControls &&
            this.getElementPendingStateControls(nestedAsset, id)}
          {showOptions && (
            <Menu dataCyLabel={`menu_${nestedAsset.name}_${nestedAsset.id}`}>
              {canUpdate && (
                <MenuItem
                  key={`add-element-${id}`}
                  onClick={() => this.toggleModal(ModalType.ADD_ELEMENT, id)}
                  startAdornment={<Icon>add</Icon>}
                >
                  Add Element
                </MenuItem>
              )}
              {showMove && (
                <MenuItem
                  key={`move-element-${id}`}
                  startAdornment={<Icon>arrow_forward</Icon>}
                  onClick={() => this.toggleModal(ModalType.MOVE_ELEMENT, id)}
                >
                  Move
                </MenuItem>
              )}
              {showReplace && (
                <MenuItem
                  key={`replace-element-${id}`}
                  startAdornment={<Icon>autorenew</Icon>}
                  onClick={() =>
                    this.toggleModal(ModalType.REPLACE_ELEMENT, id)
                  }
                >
                  Replace
                </MenuItem>
              )}
              {showRename && (
                <MenuItem
                  key={`rename-element-${id}`}
                  onClick={() => this.toggleModal(ModalType.EDIT, id)}
                  startAdornment={<Icon>edit</Icon>}
                >
                  Edit
                </MenuItem>
              )}
              {showDelete && (
                <MenuItem
                  key={`delete-element-${id}`}
                  onClick={() => this.toggleModal(ModalType.DELETE, id)}
                  startAdornment={<Icon>delete</Icon>}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          )}
        </div>
      );

      const subText = this.getAttributeSubText(nestedAsset);

      const elementLabelStyle = this.getDisplayLabelStyle(
        isPendingApproval,
        nestedAsset.changeType,
      );

      const primaryText = (
        <div style={styles.elementNameContainer}>
          <div style={elementLabelStyle}>{name}</div>
          {!R.isEmpty(subText) && (
            <Tooltip
              lightTooltip
              title={subText.replace(/,/g, ';')}
              placement="right-start"
            >
              <div style={styles.assetType}>{subText}</div>
            </Tooltip>
          )}
        </div>
      );

      const icon =
        R.isEmpty(nextElements) && !hasChildren
          ? iconUtilities.getElementIcon(markerId, isPendingApproval)
          : expanded
            ? iconUtilities.leftElementIcon(
                markerId,
                'down-arrow',
                isPendingApproval,
              )
            : iconUtilities.leftElementIcon(
                markerId,
                'right-arrow',
                isPendingApproval,
              );

      return (
        <div key={`${index}-${id}`}>
          <ListItemButton
            disableGutters
            key={id}
            style={{ ...rootStyles, paddingLeft: `${level * 1.5}rem` }}
            onClick={(): void => {
              this.navigate(element);
              onToggle(id, path, expanded, hasChildren);
            }}
          >
            {icon}
            <ListItemText
              primaryTextStyle={styles.listItemTextStyle}
              primary={primaryText}
            />
            {rightIconButton}
          </ListItemButton>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {this.renderNestedElements(
                nextElements,
                path,
                level + 1,
                element.schemaId,
              )}
            </List>
          </Collapse>
          {canUpdate && (
            // @ts-ignore
            <AddAssetElementModal
              open={this.isModalOpen(ModalType.ADD_ELEMENT, id)}
              closeModal={() => this.toggleModal(ModalType.NONE, null)}
              title="Add elements to asset"
              assetId={id}
              assetName={name}
              updateSelectedItem={updateSelectedItem}
              data-cy="addElementToRootAsset"
            />
          )}
          {showMove && (
            <MoveElementModal
              open={this.isModalOpen(ModalType.MOVE_ELEMENT, id)}
              onClose={onMoveElementClose}
              elementId={element.id}
              rootAsset={inventoryAssetDetail}
              parentSchemaId={parentSchemaId}
            />
          )}
          {showReplace && (
            <ReplaceElementModal
              open={this.isModalOpen(ModalType.REPLACE_ELEMENT, id)}
              onClose={onMoveElementClose}
              elementId={element.id}
              rootAsset={inventoryAssetDetail}
              parentSchemaId={parentSchemaId}
            />
          )}
          {showRename && (
            <RenameModal
              open={this.isModalOpen(ModalType.EDIT, id)}
              closeModal={() => this.toggleModal(ModalType.NONE, null)}
              id={id}
              name={name}
              type="element"
              renameAction={onRename}
            />
          )}
          {showDelete && (
            <Modal
              open={this.isModalOpen(ModalType.DELETE, id)}
              title="Delete Element"
              onCancel={() => this.toggleModal(ModalType.NONE, null)}
              confirmButtonText="Delete"
              ConfirmButtonProps={{ sx: { background: colors.brand.red } }}
              onConfirm={onConfirmDeleteModal}
            >
              Are you sure you want to delete this element?
            </Modal>
          )}
        </div>
      );
    });
  };

  renderTopLevelElements = (level: number = 0) => {
    const {
      inventoryAssetDetail,
      onToggle,
      selectedItem,
      updateSelectedItem,
      preferences,
      onMoveElement,
    } = this.props;

    const elementGroups = R.pathOr([], ['elementGroups'], inventoryAssetDetail);

    return elementGroups.map((elementGroup: any, index: number) => {
      const { isGroup } = elementGroup;
      const id = R.pathOr('', ['id'], elementGroup);
      const topLevelElement = isGroup ? elementGroup : elementGroup.elements[0];

      const elements = R.pathOr([], ['elements'], topLevelElement);
      const elementPath = isGroup
        ? ['elementGroups', index]
        : ['elementGroups', index, 'elements', 0];

      const rootStyles =
        selectedItem.id === id && !isGroup
          ? styles.selectedListItem
          : styles.listItem;

      const groupIcon = topLevelElement.expanded
        ? iconUtilities.leftIcon('down-arrow')
        : iconUtilities.leftIcon('right-arrow');

      const isPendingApproval = !!topLevelElement.changeType;

      const elementIcon =
        !isGroup && R.isEmpty(elements) && !topLevelElement.hasChildren
          ? iconUtilities.getElementIcon(
              topLevelElement.markerId,
              isPendingApproval,
            )
          : topLevelElement.expanded
            ? iconUtilities.leftElementIcon(
                topLevelElement.markerId,
                'down-arrow',
                isPendingApproval,
              )
            : iconUtilities.leftElementIcon(
                topLevelElement.markerId,
                'right-arrow',
                isPendingApproval,
              );

      const icon = isGroup ? groupIcon : elementIcon;

      const subText = this.getAttributeSubText(topLevelElement);

      const elementLabelStyle = this.getDisplayLabelStyle(
        isPendingApproval,
        topLevelElement.changeType,
      );

      const primaryText = !isGroup ? (
        <div style={styles.elementNameContainer}>
          <div
            style={elementLabelStyle}
            data-cy={`element_${topLevelElement.name}_${topLevelElement.id}`}
          >
            {topLevelElement.name}
          </div>
          {!R.isEmpty(subText) && (
            <Tooltip
              lightTooltip
              title={subText.replace(/,/g, ';')}
              placement="right-start"
            >
              <div style={styles.assetType}>{subText}</div>
            </Tooltip>
          )}
        </div>
      ) : (
        <div style={this.getGroupStyle(elementGroup)}>
          {topLevelElement.name}
        </div>
      );

      const onRename = (elementId: string, elementName: string): boolean => {
        const { onRenameAction } = this.props;

        onRenameAction({
          id: elementId,
          name: elementName,
          elementPath,
        });

        return true;
      };

      const onDelete = (): boolean => {
        const { onDeleteAction } = this.props;
        const path = R.dropLast(2, elementPath);

        onDeleteAction({
          id,
          elementPath: path,
        });

        return true;
      };

      const onConfirmDeleteModal = () => {
        onDelete();
        this.toggleModal(ModalType.NONE, null);
      };

      const onMoveElementClose = () => {
        onMoveElement();
        this.toggleModal(ModalType.NONE, null);
      };

      const hasPendingDelete =
        topLevelElement.changeType === ChangeType.DELETED;
      const canRename = hasAccess(
        PolicyAction.RENAME,
        inventoryAssetDetail.actions,
      );
      const hasAddAssetRolePermission = isCurrentClient([
        Client.UDOT_TMD,
        Client.ARDOT,
      ])
        ? hasRolePermissions(ROLE_SETS.ORG_ADMIN)
        : true;
      const canUpdate =
        hasAddAssetRolePermission &&
        hasAccess(PolicyAction.UPDATE, inventoryAssetDetail.actions);

      const moveElementEnabled = R.pathOr(
        false,
        ['inventory', 'moveElementEnabled'],
        preferences,
      );

      const replaceElementEnabled = R.pathOr(
        false,
        ['inventory', 'replaceElementEnabled'],
        preferences,
      );

      const showDelete = canUpdate && !hasPendingDelete;
      const showRename = canRename && !hasPendingDelete;
      const showMove =
        moveElementEnabled &&
        topLevelElement?.changeType !== ChangeType.ADDED &&
        hasAccess(PolicyAction.MOVE_ELEMENT, inventoryAssetDetail.actions);
      const showReplace =
        replaceElementEnabled &&
        topLevelElement?.changeType !== ChangeType.ADDED &&
        hasAccess(PolicyAction.REPLACE_ELEMENT, inventoryAssetDetail.actions);

      const canManageChanges = hasAccess(
        PolicyAction.MANAGE_INVENTORY_CHANGES,
        inventoryAssetDetail.actions,
      );

      const showChangeControls =
        canManageChanges &&
        isPendingApproval &&
        topLevelElement.changeType !== ChangeType.ELEMENTS_CHANGED &&
        topLevelElement.changeType !== ChangeType.PARENT_ADDED;

      const showOptions =
        showRename || showDelete || canUpdate || showMove || showReplace;

      const rightIconButton = !isGroup ? (
        <div
          className={classes.elementRightIconContainer}
          style={styles.elementRightIconContainer}
        >
          {showChangeControls &&
            this.getElementPendingStateControls(topLevelElement, id)}
          {showOptions && (
            <Menu
              dataCyLabel={`menu_${topLevelElement.name}_${topLevelElement.id}`}
            >
              {canUpdate && (
                <MenuItem
                  key={`add-element-${id}`}
                  startAdornment={<Icon>add</Icon>}
                  onClick={() => this.toggleModal(ModalType.ADD_ELEMENT, id)}
                >
                  Add Element
                </MenuItem>
              )}
              {showMove && (
                <MenuItem
                  key={`move-element-${id}`}
                  startAdornment={<Icon>arrow_forward</Icon>}
                  data-cy={`move_${topLevelElement.name}_${topLevelElement.id}`}
                  onClick={() => this.toggleModal(ModalType.MOVE_ELEMENT, id)}
                >
                  Move
                </MenuItem>
              )}
              {showReplace && (
                <MenuItem
                  key={`replace-element-${id}`}
                  startAdornment={<Icon>autorenew</Icon>}
                  data-cy={`replace_${topLevelElement.name}_${topLevelElement.id}`}
                  onClick={() =>
                    this.toggleModal(ModalType.REPLACE_ELEMENT, id)
                  }
                >
                  Replace
                </MenuItem>
              )}
              {showRename && (
                <MenuItem
                  key={`rename-element-${id}`}
                  onClick={() => this.toggleModal(ModalType.EDIT, id)}
                  data-cy={`edit_${topLevelElement.name}_${topLevelElement.id}`}
                  startAdornment={<Icon>edit</Icon>}
                >
                  Edit
                </MenuItem>
              )}
              {showDelete && (
                <MenuItem
                  key={`delete-element-${id}`}
                  data-cy={`delete_${topLevelElement.name}_${topLevelElement.id}`}
                  onClick={() => this.toggleModal(ModalType.DELETE, id)}
                  startAdornment={<Icon>delete</Icon>}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          )}
        </div>
      ) : (
        <div style={{ position: 'relative' }} />
      );

      const parentSchemaId = topLevelElement.isGroup
        ? inventoryAssetDetail.schemaId
        : topLevelElement.schemaId;

      return (
        <div key={`${index}-${id}`}>
          <ListItemButton
            disableGutters
            key={index}
            style={{ ...rootStyles, paddingLeft: `${level * 1.5}rem` }}
            onClick={(): void => {
              this.navigate(topLevelElement);
              onToggle(
                id,
                elementPath,
                topLevelElement.expanded,
                topLevelElement.hasChildren,
              );
            }}
          >
            {icon}
            <ListItemText
              primaryTextStyle={styles.listItemTextStyle}
              primary={primaryText}
            />
            {rightIconButton}
          </ListItemButton>
          <Collapse in={topLevelElement.expanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {this.renderNestedElements(
                elements,
                elementPath,
                level + 1,
                parentSchemaId,
              )}
            </List>
          </Collapse>
          {canUpdate && (
            // @ts-ignore
            <AddAssetElementModal
              open={this.isModalOpen(ModalType.ADD_ELEMENT, id)}
              closeModal={() => this.toggleModal(ModalType.NONE, null)}
              title="Add elements to asset"
              assetId={id}
              assetName={topLevelElement.name}
              updateSelectedItem={updateSelectedItem}
            />
          )}
          {showMove && (
            <MoveElementModal
              open={this.isModalOpen(ModalType.MOVE_ELEMENT, id)}
              onClose={onMoveElementClose}
              elementId={topLevelElement.id}
              rootAsset={inventoryAssetDetail}
              parentSchemaId={inventoryAssetDetail.schemaId}
            />
          )}
          {showReplace && (
            <ReplaceElementModal
              open={this.isModalOpen(ModalType.REPLACE_ELEMENT, id)}
              onClose={onMoveElementClose}
              elementId={topLevelElement.id}
              rootAsset={inventoryAssetDetail}
              parentSchemaId={inventoryAssetDetail.schemaId}
            />
          )}
          {showRename && (
            <RenameModal
              open={this.isModalOpen(ModalType.EDIT, id)}
              closeModal={() => this.toggleModal(ModalType.NONE, null)}
              id={id}
              name={topLevelElement.name}
              type="element"
              renameAction={onRename}
            />
          )}
          {showDelete && (
            <Modal
              open={this.isModalOpen(ModalType.DELETE, id)}
              title="Delete Element"
              onCancel={() => this.toggleModal(ModalType.NONE, null)}
              confirmButtonText="Delete"
              onConfirm={onConfirmDeleteModal}
            >
              Are you sure you want to delete this element?
            </Modal>
          )}
        </div>
      );
    });
  };

  render() {
    const { inventoryAssetDetail, loading, onBatchCreate } = this.props;
    const { schemaId, id, name } = inventoryAssetDetail;

    const topLevelListItems = this.renderTopLevelElements();

    const canUpdate = hasAccess(
      PolicyAction.UPDATE,
      inventoryAssetDetail.actions,
    );

    const canAdd = isCurrentClient([Client.UDOT_TMD, Client.ARDOT])
      ? hasRolePermissions(ROLE_SETS.ORG_ADMIN)
      : true;

    return loading && R.isEmpty(inventoryAssetDetail) ? (
      <Progress style={{ height: '100%' }} />
    ) : (
      <div>
        <ElementsHeader
          assetName={name}
          schemaId={schemaId}
          assetId={id}
          label="Sub Items"
          onBatchCreateAction={onBatchCreate}
          isEditDisabled={!canUpdate || !canAdd}
        />
        <List style={styles.listStyle}>{topLevelListItems}</List>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxStore): ReduxStateProps => ({
  preferences: state.preferences,
});

export default connect(mapStateToProps, null)(DetailTree);
